import register from 'preact-custom-element';
import type { FunctionComponent } from 'preact';

import { getBoolProp } from '../../../../utils/props';
import EnhancedSmartSelfieCapture from '../enhanced-smartselfie-capture/EnhancedSmartSelfieCapture';

interface Props {
  interval?: number;
  duration?: number;
  'theme-color'?: string;
  'show-navigation'?: string | boolean;
  'allow-agent-mode'?: string | boolean;
  'show-agent-mode-for-tests'?: string | boolean;
  'hide-attribution'?: string | boolean;
  'disable-image-tests'?: string | boolean;
  /**
   * Selects the liveness mechanic, not the UI: strict runs the head-pose
   * (`directions`) active-liveness flow, non-strict runs the smile flow. Both
   * render through the same Enhanced SmartSelfie views.
   */
  'use-strict-mode'?: string | boolean;
  'show-back-on-guidelines'?: string | boolean;
  /**
   * Partner opt-in for the interval capture when the
   * face-detection model can't load. Passed straight through to ESS.
   */
  'allow-legacy-selfie-fallback'?: string | boolean;
  /** Skips Enhanced SmartSelfie's guidelines screen and goes straight to capture. */
  'hide-instructions'?: string | boolean;
  /**
   * Bumped by `SelfieCaptureScreens` to force a full remount (and therefore a
   * clean capture session) on retake and on returning from document capture.
   */
  key?: string;
  hidden?: string | boolean;
}

/**
 * Thin adapter between the `<selfie-capture-screens>` router and Enhanced
 * SmartSelfie. It exists only to translate the `use-strict-mode` attribute into
 * a `liveness-mode` and to honour `hidden`; ESS owns the capture experience
 * end to end, including loading, MediaPipe retries, and the interval-capture
 * (no-detection) capture used when the model can't load.
 */
const SelfieCaptureWrapper: FunctionComponent<Props> = ({
  'use-strict-mode': useStrictModeProp = false,
  hidden: hiddenProp = false,
  ...props
}) => {
  const hidden = getBoolProp(hiddenProp);
  const useStrictMode = getBoolProp(useStrictModeProp);

  if (hidden) {
    return null;
  }

  return (
    <>
      <style>{`:host { display: block; height: 100%; }`}</style>
      <EnhancedSmartSelfieCapture
        {...(props as any)}
        liveness-mode={useStrictMode ? 'directions' : 'smile'}
      />
    </>
  );
};

if (!customElements.get('selfie-capture-wrapper')) {
  register(
    SelfieCaptureWrapper,
    'selfie-capture-wrapper',
    [
      'interval',
      'duration',
      'theme-color',
      'show-navigation',
      'allow-agent-mode',
      'show-agent-mode-for-tests',
      'hide-attribution',
      'disable-image-tests',
      'use-strict-mode',
      'show-back-on-guidelines',
      'allow-legacy-selfie-fallback',
      'hide-instructions',
      'key',
      'hidden',
    ],
    { shadow: true },
  );
}

export default SelfieCaptureWrapper;
